import { useState } from "react";

import { useAuthContext } from "@eisox/auth";
import dayjs from "@eisox/dayjs";
import { Button, CanAccess, Sidebar } from "@eisox/design-system";
import { GroupIcon, HelpIcon, MapIcon, RefreshIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { useSuspenseQueries } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";

import { authApi, groupApi } from "~/api";
import { NewsDialog } from "~/components/news-dialog";
import { SidebarItem } from "~/components/sidebar-item";
import { m } from "~/paraglide/messages.js";

export const Route = createFileRoute("/_authenticated/_no-building")({
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(groupApi.queries.all);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { user, signOut } = useAuthContext(Route.id);

  const [{ data: groups }, { data: roles }] = useSuspenseQueries({
    queries: [groupApi.queries.all, authApi.queries.roles],
  });
  const [newsOpen, setNewsOpen] = useState(false);

  const handleRedirect = () => {
    if (location.pathname) {
      window.location.assign(
        (import.meta.env.MODE === "staging" || import.meta.env.MODE === "pre-prod"
          ? import.meta.env.VITE_PROD_WEBAPP_URL
          : import.meta.env.VITE_PREPROD_WEBAPP_URL) + location.pathname,
      );
    }
  };

  const canAccessPreprod = roles.installer.length > 0;
  const displayChristmasHat = roles.installer.length > 0 && dayjs().month() === 11;
  const canAccessAssistance = roles.visitor.length === 0;

  return (
    <Sidebar.Provider>
      <Sidebar.Root displayChristmasHat={displayChristmasHat}>
        <Sidebar.Header />
        <Sidebar.Content>
          <SidebarItem to="/buildings">
            <MapIcon /> {m.my_buildings()}
          </SidebarItem>
          {groups.length > 0 && !isMobile && (
            <SidebarItem to="/groups">
              <GroupIcon /> {m.groups()}
            </SidebarItem>
          )}
          <CanAccess isAuthorized={canAccessAssistance}>
            <SidebarItem className="mt-auto" to="/resources/$" params={{ _splat: "" }}>
              <HelpIcon /> {m.assistance()}
            </SidebarItem>
          </CanAccess>
        </Sidebar.Content>
        <Sidebar.Footer
          email={user?.email ?? ""}
          firstName={user?.firstName}
          lastName={user?.lastName}
          version={import.meta.env.VITE_VERSION}
        >
          <div className="gap-2.5 flex flex-col items-start">
            <CanAccess isAuthorized={canAccessPreprod}>
              {(import.meta.env.MODE === "staging" ||
                import.meta.env.MODE === "pre-prod" ||
                import.meta.env.MODE === "prod") && (
                <Button onClick={handleRedirect} className={"text-primary-dark-blue"}>
                  {import.meta.env.MODE.toUpperCase()}
                  <RefreshIcon />
                </Button>
              )}
            </CanAccess>
            <Button className="text-primary-dark-blue" onClick={() => setNewsOpen(true)}>
              {m.new()}
            </Button>
            <Button asChild className="text-primary-dark-blue">
              <Link to="/account">{m.my_acccount()}</Link>
            </Button>
            <Button
              className="text-primary-dark-blue"
              onClick={() => {
                void signOut();
              }}
            >
              {m.sign_out()}
            </Button>
          </div>
        </Sidebar.Footer>
      </Sidebar.Root>
      <NewsDialog open={newsOpen} onOpenChange={setNewsOpen} canReadMore={canAccessAssistance} />
      <Outlet />
    </Sidebar.Provider>
  );
}
